import { configuration } from "./Configuration";
import { authenticatedFetch } from "./JWTAuthService";
import {
    ManagedFileMetaData,
    ManagedFolderListResult,
} from "@the-real-insight/tri-model";
import { PATIENT_REQUEST } from "./utilities/requestTimeout";

const baseUrl = configuration.serverUrl;

/**
 * Der Fehlertext des Servers, wenn er einen schickt — sonst der Statustext.
 *
 * Das Löschen kann der Server mit Grund verweigern (409: an der Datei hängt eine
 * Dokumentenanalyse). Der Grund steht als `error` im Antwortkörper; ein Fehler mit bloßem
 * „Conflict" ließe die Nutzerin raten, warum die Datei noch da ist.
 */
const errorFromResponse = async (response: Response, fallback: string): Promise<Error> => {
  try {
    const body = await response.json();
    if (body && typeof body.error === "string" && body.error) {
      return new Error(body.error);
    }
  } catch {
    /* kein JSON — dann der Statustext */
  }
  return new Error(`${fallback}: ${response.statusText}`);
};

/**
 * A managed-file volume the customer has. `key` is what goes into the `:volume` segment of every
 * storage URL; the literal `"default"` is still accepted by the server and means whichever volume
 * is flagged `isDefault`.
 */
export interface ManagedVolume {
  key: string;
  name: string;
  provider: "theRealInsight" | "microsoftSharePoint";
  isDefault: boolean;
  isPlatform: boolean;
  siteHostname?: string;
  sitePath?: string;
}

/**
 * Alias the server resolves to whichever volume the customer marked default. Used by the read-only
 * pickers before their volume list has loaded.
 */
export const DEFAULT_VOLUME_KEY = "default";

/**
 * The internal S3 volume every customer always has (`src/volumeRegistry.ts` on the server).
 *
 * Writes address it explicitly rather than through `DEFAULT_VOLUME_KEY`: the default volume can be a
 * SharePoint one — that setting governs where *generated* files land — and the file manager must not
 * silently become a write surface for a customer's SharePoint site.
 */
export const PLATFORM_VOLUME_KEY = "managed-documents";

const getVolumes = async (customerID: string): Promise<ManagedVolume[]> => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/volumes`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch volumes: ${response.statusText}`);
  }

  const result = await response.json();
  return (result?.volumes ?? []) as ManagedVolume[];
};

const getStorage = async (
  customerID: string,
  volume: string = DEFAULT_VOLUME_KEY,
  /** Nur den Ast unter diesem Pfad. Ohne Angabe das ganze Volume, wie bisher. */
  path?: string
): Promise<ManagedFolderListResult> => {
  const query = path ? `?path=${encodeURIComponent(path)}` : "";
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}${query}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  // Handle 429 Too Many Requests error gracefully
  if (response.status === 429) {
    console.warn("Rate limited by server, skipping storage update");
    throw new Error("Rate limited - too many requests");
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch storage data: ${response.statusText}`);
  }

  const result = await response.json();
  return result as ManagedFolderListResult;
};

const createFolder = async (
  customerID: string,
  volume: string,
  path: string
): Promise<ManagedFileMetaData> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        path,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to create folder: ${response.statusText}`);
  }

  const result = await response.json();
  return result as ManagedFileMetaData;
};

const deleteObject = async (
  customerID: string,
  volume: string,
  path: string
): Promise<{ message: string; path: string; type: "file" | "folder" }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/delete`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        path,
      }),
    }
  );

  if (!response.ok) {
    throw await errorFromResponse(response, "Failed to delete object");
  }

  return response.json();
};

const renameFolder = async (
  customerID: string,
  volume: string,
  folder: string,
  newName: string
): Promise<{ message: string; metadata: ManagedFileMetaData }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/update`,
    {
      method: "POST",
      body: JSON.stringify({
        action: "renameFolder",
        folder,
        newName,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to rename folder: ${response.statusText}`);
  }

  return response.json();
};

const getFileOrListFolder = async (
  customerID: string,
  volume: string,
  path: string,
  download: boolean = false
): Promise<Blob | ManagedFolderListResult> => {
  const url = `${baseUrl}/${customerID}/storage/${volume}/${path}${
    download ? "?download=true" : ""
  }`;
  const response = await authenticatedFetch(url, {
    method: "GET",
    headers: {
      Accept: "*/*",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get file or folder: ${response.statusText}`);
  }

  // Check if it's a JSON response (folder contents) or binary (file)
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  } else {
    return response.blob();
  }
};

const downloadFile = async (
  customerID: string,
  volume: string,
  path: string
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}${
      path.startsWith("/") ? path : "/" + path
    }?download=true`,
    {
      method: "GET",
      headers: {
        Accept: "*/*",
      },
      signal: PATIENT_REQUEST,
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.statusText}`);
  }

  // Get the content type from the response headers
  const contentType = response.headers.get("content-type");
  const contentDisposition = response.headers.get("content-disposition");

  // With the new download endpoint, we should get binary content directly
  const blob = await response.blob();

  return {
    blob,
    contentType,
    contentDisposition,
    filename: path.split("/").pop() || "download",
  };
};

const uploadFiles = async (
  customerID: string,
  volume: string,
  files: File[],
  path: string = "/",
  overwrite: boolean = false
): Promise<{ files: ManagedFileMetaData[] }> => {
  const form = new FormData();
  form.append("path", path);
  form.append("overwrite", overwrite.toString());
  files.forEach((file) => {
    form.append("files", file);
  });

  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/upload`,
    {
      method: "POST",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to upload files: ${response.statusText}`);
  }

  return response.json();
};

const uploadFileToFolder = async (
  customerID: string,
  volume: string,
  folder: string,
  file: File
): Promise<ManagedFileMetaData> => {
  const form = new FormData();

  form.append("file", file);

  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/upload`,
    {
      method: "POST",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.statusText}`);
  }

  return response.json();
};

const renameFile = async (
  customerID: string,
  volume: string,
  file: string,
  newName: string
): Promise<{ message: string; metadata: ManagedFileMetaData }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/update`,
    {
      method: "POST",
      body: JSON.stringify({
        action: "renameFile",
        file,
        newName,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to rename file: ${response.statusText}`);
  }

  return response.json();
};

const moveFile = async (
  customerID: string,
  volume: string,
  sourcePath: string,
  destinationPath: string
): Promise<{ message: string; metadata: ManagedFileMetaData }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/update`,
    {
      method: "POST",
      body: JSON.stringify({
        action: "moveFile",
        sourcePath,
        destinationPath,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to move file: ${response.statusText}`);
  }

  return response.json();
};

const moveFolder = async (
  customerID: string,
  volume: string,
  sourcePath: string,
  destinationPath: string
): Promise<{ message: string; metadata: ManagedFileMetaData }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/update`,
    {
      method: "POST",
      body: JSON.stringify({
        action: "moveFolder",
        sourcePath,
        destinationPath,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to move folder: ${response.statusText}`);
  }

  return response.json();
};

const updateMetadata = async (
  customerID: string,
  volume: string,
  path: string,
  metadata: any
): Promise<{ message: string; result: ManagedFileMetaData }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/update`,
    {
      method: "POST",
      body: JSON.stringify({
        action: "updateMetadata",
        path,
        metadata,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update metadata: ${response.statusText}`);
  }

  return response.json();
};

const getFileContent = async (
  customerID: string,
  volume: string,
  path: string
): Promise<string> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}${
      path.startsWith("/") ? path : "/" + path
    }?getContent=true`,
    {
      method: "GET",
      headers: {
        Accept: "text/plain",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to get file content: ${response.statusText}`);
  }

  return response.text();
};

const updateFileContent = async (
  customerID: string,
  volume: string,
  path: string,
  content: string
): Promise<{ message: string; metadata: ManagedFileMetaData }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/update`,
    {
      method: "POST",
      body: JSON.stringify({
        action: "updateFileContent",
        path,
        content,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update file content: ${response.statusText}`);
  }

  return response.json();
};

const deleteFile = async (
  customerID: string,
  volume: string,
  path: string
): Promise<{ message: string; path: string; type: "file" | "folder" }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/delete`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        path,
      }),
    }
  );

  if (!response.ok) {
    throw await errorFromResponse(response, "Failed to delete file");
  }

  return response.json();
};

/**
 * Ensure a ManagedFile document (and thus an _id) exists for a backend file that
 * was never uploaded through the platform (e.g. a SharePoint file browsed via the
 * picker). Returns the managed file metadata including _id.
 */
const registerFile = async (
  customerID: string,
  volume: string,
  path: string,
  hint?: { providerId?: string; name?: string; webUrl?: string }
): Promise<ManagedFileMetaData> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/storage/${volume}/register`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, ...hint }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to register file: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Fetch lightweight metadata (name/path) for a ManagedFile by its id. Used to
 * resolve a stored file-id reference back to a filename for display, including
 * files that live in a subfolder and are absent from the root listing.
 *
 * Returns `null` when the file no longer exists (HTTP 404) so callers can
 * distinguish a stale/orphaned reference from a transient failure (which throws).
 */
const getFileMeta = async (
  customerID: string,
  fileId: string
): Promise<ManagedFileMetaData | null> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/file-meta/${fileId}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch file metadata: ${response.statusText}`);
  }

  return response.json();
};

export const storageAPI = {
  // Core storage operations
  getVolumes,
  getStorage,
  getFileOrListFolder,
  registerFile,
  getFileMeta,

  // Folder operations
  createFolder,
  deleteObject,
  renameFolder,
  moveFolder,

  // File operations
  downloadFile,
  uploadFiles,
  uploadFileToFolder,
  renameFile,
  moveFile,
  deleteFile,

  // File content operations
  getFileContent,
  updateFileContent,

  // Metadata operations
  updateMetadata,
};
